import initAnalytics from 'public/assets/js/analytics';
import { trackEvent } from 'public/assets/js/analytics/track';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import useAdBlockDetector from 'src/hooks/useAdBlockDetector';
import { isProduction } from 'src/lib/constants';
import { Events } from 'src/lib/trackingEvents';
import { LDProvider } from '../LaunchDarklyContext/LaunchDarklyContext';
import { ContextValue, TrackFunction } from './AnalyticsContextTypes';

const navigatePage = (node: HTMLElement) => {
  const href = node.getAttribute('href');
  const target = node.getAttribute('target');
  if (href) {
    if (target) {
      return window.open(href, target);
    }
    window.location.href = href;
  } else {
    throw new Error('no href specified');
  }
};

// defaultValue used for context instances not wrapped by Provider,
// and required to prevent TypeError and TS compile errors.
// https://reactjs.org/docs/context.html#reactcreatecontext
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/34854
const AnalyticsContext = React.createContext<ContextValue>({
  track: () => {
    return undefined;
  },
});

/**
 * Script must be included once per page as early as possible to enable this context provider.
 * Note: script is very outdated and should be upgraded or replaced with
 * `@segment/analytics-next` once all analytics are consolidated.
 */
export const AnalyticsScript: FC = () => {
  return (
    <script
      dangerouslySetInnerHTML={{
        /**
         * First introduced in 2021-02-24, we disable Segment analytics for the Algolia docs
         * crawler (which powers docs search) as this has proven to be problematic in the
         * past. Unlikely to still be the case, but leaving in to be safe for now.
         *
         * @see [SEV-545]([redacted]#####################################################################)
         */
        __html: `
        navigator.userAgent.indexOf('Algolia') < 0 && !function(){
          var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t,e){var n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src="https://cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.1.0";
        }}();
        `,
      }}
    />
  );
};

/**
 * Initialize analytics and track page view. This must NOT be called
 * within `<Head>` as `useEffect`s will not run there.
 *
 * Must not be called before `<AnalyticsScript />` is rendered.
 */
export const InitAnalytics: FC = () => {
  useEffect(() => {
    initAnalytics();
  }, []);

  return null;
};

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
  const hasAdBlocker = useAdBlockDetector();
  // `track` reads the detector's latest answer when it's called, so it stays the
  // same function. The detector resolves just after hydration, and a new `track`
  // would change this context (and LDProvider's) while lazy icons are still
  // hydrating, which makes React throw #421 and client-render them.
  const hasAdBlockerRef = useRef(hasAdBlocker);
  useEffect(() => {
    hasAdBlockerRef.current = hasAdBlocker;
  }, [hasAdBlocker]);

  // TODO: we are commingling `action.type` and `action.payload.trackingId`. This might
  //  make  sense if we exclusively branched on `action.type` and only sent
  //  `trackingId` to Segment, but that's not the case throughout this
  //  file. We should refactor this into just `action.type`.
  const track: TrackFunction = useCallback(
    (action, callback = null, retries = 0) => {
      if (typeof window === 'undefined') {
        return;
      }

      const analyticsInitialized = window?.analytics?.initialized || false;

      try {
        if (action.type === Events.BUTTON_CLICKED) {
          trackButtonClick();
        } else if (callback != null) {
          trackEventWithCallback();
        } else {
          // We can retry if necessary, because nothing is blocking
          if (!analyticsInitialized) {
            if (retries < 5) {
              setTimeout(() => {
                return track(action, callback, retries + 1);
              }, 500);
              return;
            } else {
              if (!isProduction) {
                // eslint-disable-next-line no-console
                console.log('Analytics event gave up after retries');
              }
              return;
            }
          }
          trackEvent({
            event: action.type,
            payload: action.payload,
          });
        }
      } catch (e) {
        // log an error to avoid future confusion when debugging this
        console.error(e); // eslint-disable-line
      }

      /**
       * Track a button click event -- add some special logic here
       */
      function trackButtonClick() {
        // use trackingId if provided as event name for tracking CTA button clicks
        // otherwise we aren't tracking button clicks
        if (action.payload?.trackingId) {
          const {
            trackingId,
            event: mouseEvent,
            currentTarget,
            ...payload
          } = action.payload;

          // We will redirect to intended href after handling tracking
          mouseEvent.preventDefault();

          // Because some ad blockers might cause our trackEvent call to fail
          // we don't want to risk the button not working
          analyticsInitialized && !hasAdBlockerRef.current
            ? trackEvent({
                event: trackingId,
                payload,
                callback: () => {
                  navigatePage(currentTarget);
                },
              })
            : // Redirect even if tracking is disabled / opted-out
              navigatePage(currentTarget);
        }
      }

      /**
       * Track an event that has a callback. Like the button logic, we won't
       * block the callback from happening if tracking fails.
       */
      function trackEventWithCallback() {
        analyticsInitialized && !hasAdBlockerRef.current
          ? trackEvent({
              event: action.type,
              payload: action.payload,
              callback,
            })
          : // Run the callback even if tracking is disabled / opted-out
            callback();
      }
    },
    [],
  );

  const value = useMemo(() => {
    return { track };
  }, [track]);

  return (
    <AnalyticsContext.Provider value={value}>
      <LDProvider>{children}</LDProvider>
    </AnalyticsContext.Provider>
  );
};

export const useTrack = (): TrackFunction => {
  return useContext(AnalyticsContext).track;
};

export default AnalyticsContext;
